import React, { useState, useEffect, useCallback } from "react";
import { Icon } from "@iconify/react";
import StatCard from "../components/common/StatCard";
import PageHeader from "../components/common/PageHeader";
import { useNavigate } from "react-router-dom";
import DashboardServices from "../services/api/dashboard";
// import { getProductPrice, getDefaultPlan, formatCurrency, hasPlans } from '../utils/productPricing';
import CurrencyFormatter from '../utils/CurrencyFormatter';
import { API_URL } from '../config/api';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale,
} from 'chart.js';
import { Line, Pie, Bar, Doughnut, PolarArea, Radar } from 'react-chartjs-2';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  ArcElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  RadialLinearScale
);

const DashBoardLayerTen = () => {
  const navigate = useNavigate();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Estado para controlar o tema e forçar re-renderização
  const [currentTheme, setCurrentTheme] = useState(
    document.documentElement.getAttribute('data-theme') || 'light'
  );

  // Escutar mudanças no tema
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          const newTheme = document.documentElement.getAttribute('data-theme') || 'light';
          setCurrentTheme(newTheme);
          // Forçar re-renderização dos gráficos
          setTimeout(() => {
            window.dispatchEvent(new Event('resize'));
          }, 100);
        }
      });
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['data-theme']
    });

    return () => observer.disconnect();
  }, []);

  // Função para detectar dark mode e retornar cores apropriadas
  const getChartColors = () => {
    const isDarkMode = currentTheme === 'dark';
    
    if (isDarkMode) {
      return {
        sales: {
          borderColor: '#64B5F6', // Azul claro para dark mode
          backgroundColor: 'rgba(100, 181, 246, 0.15)',
          pointBackgroundColor: '#64B5F6',
          pointBorderColor: '#ffffff',
        },
        commissions: {
          borderColor: '#FFB74D', // Laranja claro para dark mode
          backgroundColor: (context) => {
            const chart = context.chart;
            const {ctx, chartArea} = chart;
            if (!chartArea) return null;
            
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, 'rgba(255, 183, 77, 0.4)');
            gradient.addColorStop(1, 'rgba(255, 183, 77, 0.1)');
            return gradient;
          },
          pointBackgroundColor: '#FFB74D',
          pointBorderColor: '#ffffff',
        },
        textColor: '#ffffff',
        gridColor: '#404040',
        tickColor: '#b3b3b3'
      };
    } else {
      // CORES ORIGINAIS DO LIGHT MODE (como estava antes)
      return {
        sales: {
          borderColor: '#3b82f6', // Azul original
          backgroundColor: 'rgba(59, 130, 246, 0.15)',
          pointBackgroundColor: '#3b82f6',
          pointBorderColor: '#ffffff',
        },
        commissions: {
          borderColor: '#ff4500', // Laranja original
          backgroundColor: (context) => {
            const chart = context.chart;
            const {ctx, chartArea} = chart;
            if (!chartArea) return null;
            
            const gradient = ctx.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
            gradient.addColorStop(0, 'rgba(255, 69, 0, 0.4)'); // Laranja vivo no topo
            gradient.addColorStop(1, 'rgba(255, 165, 0, 0.1)'); // Laranja claro embaixo
            return gradient;
          },
          pointBackgroundColor: '#ff4500',
          pointBorderColor: '#ffffff',
        },
        textColor: '#374151', // Texto escuro original
        gridColor: 'rgba(0, 0, 0, 0.05)', // Grid original
        tickColor: '#6b7280' // Ticks originais
      };
    }
  };
  const [chartPeriod, setChartPeriod] = useState('3months');

  // Estado para dados de vendas mensais
  const [chartData, setChartData] = useState(null);
  const [chartLoading, setChartLoading] = useState(false);

  // Carrega série mensal a partir do adapter V2.
  // Comissões: V2 não retorna agregado por venda — adapter marca como
  // "indisponível" (commissionsData = null). Não usamos heurística.
  const loadSalesData = useCallback(async (period) => {
    setChartLoading(true);
    try {
      // Reutiliza as vendas já carregadas no getDashboardData (evita refetch)
      const precomputedSales = dashboardData?._allSalesV2 || null;
      const response = await DashboardServices.getSalesByMonth(period, precomputedSales);
      if (response.success) {
        setChartData(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar dados de vendas mensais:', error);
      // Sem fallback mockado — mostra dataset vazio para não exibir números errados.
      setChartData({ months: [], salesData: [], commissionsData: null, commissionsUnavailable: true });
    } finally {
      setChartLoading(false);
    }
  }, [dashboardData]);

  // Calcular escala máxima baseada nos dados
  const getMaxScale = (data) => {
    const maxValue = Math.max(...data);
    if (!maxValue || maxValue <= 0) return 1000; // Fallback para evitar NaN
    const magnitude = Math.pow(10, Math.floor(Math.log10(maxValue)));
    return Math.ceil(maxValue / magnitude) * magnitude;
  };

    // Carregar dados do dashboard (V2 — DashboardServices é adapter sobre balancesV2API + salesV2API + productsV2API + withdrawalsV2API)
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await DashboardServices.getDashboardData();
        setDashboardData(response.data);
    } catch (error) {
        console.error('Erro ao carregar dashboard:', error);
        console.error('Erro completo:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, []);

  // Carregar dados de vendas quando o período mudar
  useEffect(() => {
    if (dashboardData) {
      loadSalesData(chartPeriod);
    }
  }, [chartPeriod, dashboardData, loadSalesData]);

  // Carregar dados iniciais de vendas
  useEffect(() => {
    if (dashboardData && !chartData) {
      loadSalesData('3months'); // Sempre começar com 3 meses
    }
  }, [dashboardData, chartData, loadSalesData]);

  // ✅ Usando CurrencyFormatter centralizado (valores em centavos)
  const formatCurrencyLocal = (value) => {
    return CurrencyFormatter.centsToFormatted(value);
  };

  // Formatação de número
  const formatNumber = (value) => {
    return new Intl.NumberFormat('pt-BR').format(value);
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '400px' }}>
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Carregando...</span>
          </div>
          <p className="mt-3">Carregando dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid">
        <div className="alert alert-danger" role="alert">
          <h4 className="alert-heading">Erro ao carregar Dashboard!</h4>
          <p><strong>Erro:</strong> {error}</p>
          <p><strong>Token presente:</strong> {localStorage.getItem('auth_token') || localStorage.getItem('token') ? 'Sim' : 'Não'}</p>
          <p><strong>API URL:</strong> {API_URL}</p>
          <hr />
          <button 
            className="btn btn-outline-danger me-2"
            onClick={() => window.location.reload()}
          >
            Tentar Novamente
          </button>
          <button 
            className="btn btn-outline-info me-2"
            onClick={() => console.log('Debug info:', {
              token: localStorage.getItem('auth_token') || localStorage.getItem('token'),
              apiUrl: API_URL,
              error: error
            })}
          >
            Debug no Console
          </button>
          {!(localStorage.getItem('auth_token') || localStorage.getItem('token')) && (
            <button 
              className="btn btn-outline-warning"
              onClick={() => {
                // Definir token fake para teste
                localStorage.setItem('auth_token', 'fake-jwt-token-1');
                window.location.reload();
              }}
            >
              Fazer Login de Teste
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      {/* Header da página */}
      <PageHeader
        icon="mdi:view-dashboard"
        title="Dashboard"
      />

      {/* Cards do Topo */}
      <div className="row g-3 top-cards" style={{ marginBottom: '24px' }}>
        <div className="col-md-3">
          <StatCard
            title="Vendas de hoje"
            value={formatCurrencyLocal(dashboardData?.todaySales?.amount || 0)}
            subtitle={(() => {
              const yesterdayAmount = dashboardData?.yesterdaySales?.amount || 0;
              const todayAmount = dashboardData?.todaySales?.amount || 0;
              const isPositive = todayAmount >= yesterdayAmount;
              const arrow = isPositive ? '↗' : '↘';

              return (
                <span>
                  {arrow}{' '}
                  <span
                    onClick={() => navigate('/sales-management', { state: { dateRange: 'yesterday' } })}
                    style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
                  >
                    Vendas ontem: {formatCurrencyLocal(yesterdayAmount)}
                  </span>
                </span>
              );
            })()}
            icon="mdi:chart-line"
            variant="blue"
            onClickValue={() => navigate('/sales-management', { state: { dateRange: 'today' } })}
          />
        </div>

        <div className="col-md-3">
          <StatCard
            title="Saldo Total"
            value={formatCurrencyLocal(dashboardData?.totalBalance || 0)}
            subtitle={
              <span
                onClick={() => navigate('/sales-management')}
                style={{ cursor: 'pointer', textDecoration: 'underline', textDecorationStyle: 'dotted' }}
              >
                ↗ {formatNumber(dashboardData?.totalSales?.count || 0)} vendas
              </span>
            }
            icon="mdi:wallet"
            variant="purple"
          />
        </div>

        <div className="col-md-3">
          <StatCard
            title="Saldo para Saque"
            value={formatCurrencyLocal(dashboardData?.availableBalance || 0)}
            subtitle={
              <div className="d-flex flex-column" style={{ gap: '6px', marginTop: '4px' }}>
                <span>✓ Liberado para saque</span>
                <span
                  onClick={() => navigate('/withdrawals')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '3px 10px',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(56, 142, 60, 0.12)',
                    color: '#388E3C',
                    fontSize: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    alignSelf: 'flex-start',
                    border: '1px solid rgba(56, 142, 60, 0.25)',
                  }}
                >
                  <Icon icon="mdi:cash" style={{ fontSize: '12px' }} />
                  Solicitar Saque
                </span>
              </div>
            }
            icon="mdi:cash-multiple"
            variant="green"
          />
        </div>

        <div className="col-md-3">
          <StatCard
            title="Saldo a Liberar"
            value={formatCurrencyLocal(dashboardData?.pendingBalance || 0)}
            subtitle={
              <div className="d-flex flex-column" style={{ gap: '6px' }}>
                <span>↗ Antecipação: {formatCurrencyLocal(dashboardData?.anticipationBalance || 0)}</span>
                <span
                  onClick={() => navigate('/withdrawals')}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    padding: '3px 10px',
                    borderRadius: '6px',
                    backgroundColor: 'rgba(245, 124, 0, 0.12)',
                    color: '#F57C00',
                    fontSize: '10px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    alignSelf: 'flex-start',
                    border: '1px solid rgba(245, 124, 0, 0.25)',
                  }}
                >
                  <Icon icon="mdi:lightning-bolt" style={{ fontSize: '12px' }} />
                  Antecipação
                </span>
              </div>
            }
            icon="mdi:trending-up"
            variant="orange"
          />
        </div>
      </div>

      {/* Segunda Linha - Gráficos */}
      <div className="row g-3" style={{ marginBottom: '24px' }}>
        {/* Vendas & Comissões */}
        <div className="col-lg-8">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px', minHeight: '420px' }}>
            <div className="card-header dashboard-card-header bg-white border-bottom-0 pb-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="card-title mb-0 d-flex align-items-center" style={{ fontSize: '14px', fontWeight: '600', display: 'block' }}>
                  <Icon icon="mdi:chart-line" className="me-2 text-primary" style={{ fontSize: '16px' }} />
                  Vendas & Comissões
                </span>
                <select 
                  className="form-select form-select-sm" 
                  style={{ width: '170px', fontSize: '12px' }}
                  value={chartPeriod}
                  onChange={(e) => setChartPeriod(e.target.value)}
                >
                  <option value="3months">Últimos 3 meses</option>
                  <option value="6months">Últimos 6 meses</option>
                  <option value="12months">Este ano</option>
                </select>
              </div>
            </div>
            <div className="card-body pt-2">
                            <div style={{ height: '350px' }}>
                {chartLoading ? (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Carregando...</span>
                    </div>
                  </div>
                ) : chartData ? (
                  (() => {
                    const hasCommissions = Array.isArray(chartData.commissionsData);
                    const maxSales = getMaxScale(
                      hasCommissions
                        ? [...chartData.salesData, ...chartData.commissionsData]
                        : [...chartData.salesData]
                    );
                    const colors = getChartColors();
                    const datasets = [
                      {
                        label: 'Vendas (R$)',
                        data: chartData.salesData,
                        borderColor: currentTheme === 'dark' ? '#64B5F6' : '#3b82f6',
                        backgroundColor: colors.sales.backgroundColor,
                        tension: 0.4,
                        fill: true,
                        pointBackgroundColor: colors.sales.pointBackgroundColor,
                        pointBorderColor: colors.sales.pointBorderColor,
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                      },
                    ];
                    if (hasCommissions) {
                      datasets.push({
                        label: 'Comissões (R$)',
                        data: chartData.commissionsData,
                        borderColor: currentTheme === 'dark' ? '#FFB74D' : '#ff4500',
                        backgroundColor: colors.commissions.backgroundColor,
                        tension: 0.4,
                        fill: true,
                        pointBackgroundColor: colors.commissions.pointBackgroundColor,
                        pointBorderColor: colors.commissions.pointBorderColor,
                        pointBorderWidth: 2,
                        pointRadius: 4,
                        pointHoverRadius: 6,
                      });
                    }

  return (
                    <>
                    {chartData.commissionsUnavailable && (
                      <div
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                          padding: '2px 8px',
                          marginBottom: '6px',
                          borderRadius: '6px',
                          backgroundColor: 'rgba(108, 117, 125, 0.10)',
                          color: '#6c757d',
                          fontSize: '10px',
                          fontWeight: 600,
                          border: '1px solid rgba(108, 117, 125, 0.25)',
                        }}
                        title="Agregação de comissão por mês ainda não exposta pelo backend V2."
                      >
                        <Icon icon="mdi:information-outline" style={{ fontSize: '12px' }} />
                        Comissões: indisponível
                      </div>
                    )}
                    <Line
                      data={{
                        labels: chartData.months,
                        datasets,
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        interaction: {
                          mode: 'index',
                          intersect: false,
                        },
                        elements: {
                          line: {
                            tension: 0.4
                          }
                        },
                        plugins: {
                          legend: {
                            display: true,
                            position: 'top',
                            labels: {
                              usePointStyle: true,
                              pointStyle: 'circle',
                              font: {
                                size: 11
                              },
                              color: colors.textColor,
                              padding: 15,
                            }
                          },
                          tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.85)',
                            titleColor: '#fff',
                            bodyColor: '#fff',
                            cornerRadius: 6,
                            padding: 8,
                            callbacks: {
                              label: function(context) {
                                return `${context.dataset.label}: ${formatCurrencyLocal(context.parsed.y)}`;
                              }
                            }
                          }
                        },
                        scales: {
                          x: {
                            grid: {
                              display: false,
                            },
                            ticks: {
                              font: {
                                size: 10
                              },
                              color: currentTheme === 'dark' ? '#b3b3b3' : '#6b7280'
                            }
                          },
                          y: {
                            type: 'linear',
                            display: true,
                            position: 'left',
                            min: 0,
                            max: maxSales,
                            grid: {
                              color: currentTheme === 'dark' ? '#404040' : 'rgba(0, 0, 0, 0.05)',
                            },
                            ticks: {
                              font: {
                                size: 10
                              },
                              color: currentTheme === 'dark' ? '#b3b3b3' : '#6b7280',
                              callback: function(value) {
                                return formatCurrencyLocal(value);
                              }
                            }
                          }
                        }
                      }}
                    />
                    </>
                    );
                  })()
                ) : (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <span className="text-muted">Carregando dados...</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Balanço Geral */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm" style={{ borderRadius: '12px', minHeight: '420px' }}>
            <div className="card-header dashboard-card-header bg-white border-bottom-0 pb-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="card-title mb-0 d-flex align-items-center" style={{ fontSize: '14px', fontWeight: '600', display: 'block' }}>
                  <Icon icon="mdi:chart-donut" className="me-2 text-primary" style={{ fontSize: '16px' }} />
                  Balanço Geral
                </span>
              </div>
            </div>
            <div className="card-body pt-2" style={{ position: 'relative' }}>
              {/* Títulos */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px', padding: '0 30px' }}>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#6c757d' }}>
                  Pagamento After pay
                </span>
                <span style={{ fontSize: '12px', fontWeight: '600', color: '#6c757d', textAlign: 'right' }}>
                  Pagamento Antecipado
                </span>
              </div>
              
              {/* Linha Divisória */}
              {/* <div style={{ 
                position: 'absolute', 
                left: '59%', 
                top: '-48px', 
                height: '450px', 
                width: '4px', 
                backgroundColor: currentTheme === 'dark' ? '#404040' : '#000', 
                transform: 'translateX(-53px)',
                zIndex: 10 
              }}></div>
               */}
              {/* Gráficos Semicirculares */}
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '250px', marginLeft: '-60px' }}>
                {/* AfterPay - Lado Esquerdo */}
                <div style={{ width: '250px', height: '230px', marginRight: '-72px', position: 'relative', zIndex: 10 }}>
                  <Doughnut
                      data={{
                        labels: ['Pedidos Pagos', 'Pedidos em Trânsito', 'Pagamentos Pendentes', 'Pedidos Frustrados'],
                        datasets: [{
                        data: [
                          dashboardData?.semicircleData?.afterpay?.pedidos_pagos || 0,
                          dashboardData?.semicircleData?.afterpay?.pedidos_transito || 0,
                          dashboardData?.semicircleData?.afterpay?.pagamentos_pendentes || 0,
                          dashboardData?.semicircleData?.afterpay?.pedidos_frustrados || 0
                        ],
                          backgroundColor: ['#28a745', '#ffc107', '#fd7e14', '#dc3545'],
                          borderWidth: 0,
                          circumference: 180,
                          rotation: 180
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(0, 0, 0, 0.85)',
                            titleColor: '#fff',
                            bodyColor: '#fff',
                            cornerRadius: 6,
                            padding: 8,
                            callbacks: {
                              label: function(context) {
                                const value = context.parsed || 0;
                                const total = dashboardData?.semicircleData?.afterpay?.total || 0;
                                const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                                return `${context.label}: ${value} (${percentage}%)`;
                              }
                            }
                          }
                        },
                        cutout: '75%'
                      }}
                    />
                    <div key={`afterpay-${currentTheme}`} style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center',
                      marginLeft: '1.25rem'
                    }}>
                      <div style={{ 
                        fontSize: '9px', 
                        fontWeight: '600', 
                        // color: currentTheme === 'dark' ? '#b3b3b3' : '#000', 
                      }}>
                        AGENDAMENTOS
                      </div>
                      <div style={{ 
                        fontSize: '18px', 
                        fontWeight: '700', 
                        // color: currentTheme === 'dark' ? '#ffffff' : '#000' 
                      }}>
                          {dashboardData?.semicircleData?.afterpay?.total || 0}
                      </div>
                    </div>
                </div>

                {/* Pagamento Antecipado - Lado Direito */}
                <div style={{ width: '135px', height: '230px', position: 'relative', zIndex: 1 }}>
                  <Doughnut
                      data={{
                        labels: ['Pagamento Aprovado', 'Pagamentos Pendentes', 'Cancelados', 'Reembolsados'],
                        datasets: [{
                        data: [
                          dashboardData?.semicircleData?.antecipado?.pagamento_aprovado || 0,
                          dashboardData?.semicircleData?.antecipado?.pagamentos_pendentes || 0,
                          dashboardData?.semicircleData?.antecipado?.cancelados || 0,
                          dashboardData?.semicircleData?.antecipado?.reembolsados || 0
                        ],
                          backgroundColor: ['#007bff', '#fd7e14', '#6c757d', '#dc3545'],
                          borderWidth: 0,
                          circumference: 180,
                          rotation: 0
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            enabled: true,
                            backgroundColor: 'rgba(0, 0, 0, 0.85)',
                            titleColor: '#fff',
                            bodyColor: '#fff',
                            cornerRadius: 6,
                            padding: 8,
                            callbacks: {
                              label: function(context) {
                                const value = context.parsed || 0;
                                const total = dashboardData?.semicircleData?.antecipado?.total || 0;
                                const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
                                return `${context.label}: ${value} (${percentage}%)`;
                              }
                            }
                          }
                        },
                        cutout: '75%'
                      }}
                    />
                    <div key={`antecipado-${currentTheme}`} style={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      textAlign: 'center'
                    }}>
                      <div style={{ 
                        fontSize: '9px', 
                        fontWeight: '600', 
                        // color: currentTheme === 'dark' ? '#b3b3b3' : '#000' 
                      }}>
                        PEDIDO GERADO
                      </div>
                      <div style={{ 
                        fontSize: '18px', 
                        fontWeight: '700', 
                        // color: currentTheme === 'dark' ? '#ffffff' : '#000' 
                      }}>
                          {dashboardData?.semicircleData?.antecipado?.total || 0}
                      </div>
                    </div>
                </div>
              </div>

              {/* Legendas */}
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '15px', padding: '0 20px' }}>
                <div style={{ fontSize: '8px' }}>
                  <div className="d-flex align-items-center mb-1">
                    <div style={{ width: '8px', height: '8px', backgroundColor: '#28a745', marginRight: '4px', borderRadius: '2px' }}></div>
                    <span>Pedidos Pagos</span>
                  </div>
                  <div className="d-flex align-items-center mb-1">
                    <div style={{ width: '8px', height: '8px', backgroundColor: '#ffc107', marginRight: '4px', borderRadius: '2px' }}></div>
                    <span>Pedidos em Trânsito</span>
                  </div>
                  <div className="d-flex align-items-center mb-1">
                    <div style={{ width: '8px', height: '8px', backgroundColor: '#fd7e14', marginRight: '4px', borderRadius: '2px' }}></div>
                    <span>Pagamentos Pendentes</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <div style={{ width: '8px', height: '8px', backgroundColor: '#dc3545', marginRight: '4px', borderRadius: '2px' }}></div>
                    <span>Pedidos Frustrados</span>
                  </div>
                </div>
                <div style={{ fontSize: '8px' }}>
                  <div className="d-flex align-items-center mb-1">
                    <div style={{ width: '8px', height: '8px', backgroundColor: '#007bff', marginRight: '4px', borderRadius: '2px' }}></div>
                    <span>Pagamento Aprovado</span>
                  </div>
                  <div className="d-flex align-items-center mb-1">
                    <div style={{ width: '8px', height: '8px', backgroundColor: '#fd7e14', marginRight: '4px', borderRadius: '2px' }}></div>
                    <span>Pagamentos Pendentes</span>
                  </div>
                  <div className="d-flex align-items-center mb-1">
                    <div style={{ width: '8px', height: '8px', backgroundColor: '#6c757d', marginRight: '4px', borderRadius: '2px' }}></div>
                    <span>Cancelados</span>
                  </div>
                  <div className="d-flex align-items-center">
                    <div style={{ width: '8px', height: '8px', backgroundColor: '#dc3545', marginRight: '4px', borderRadius: '2px' }}></div>
                    <span>Reembolsados</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terceira Linha */}
      <div className="row g-3" style={{ marginBottom: '24px' }}>
        {/* Eficiência Logística */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-header dashboard-card-header bg-white border-bottom-0 pb-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="card-title mb-0 d-flex align-items-center" style={{ fontSize: '14px', fontWeight: '600', display: 'block' }}>
                  <Icon icon="mdi:truck-fast" className="me-2 text-primary" style={{ fontSize: '16px' }} />
                  Eficiência Logística
                </span>
              </div>
            </div>
            <div className="card-body pt-2">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="text-muted" style={{ fontSize: '12px', fontWeight: '500' }}>Taxa de Eficiência</span>
                <span className="badge bg-success" style={{ fontSize: '12px' }}>
                  {dashboardData?.logisticsEfficiency?.completed || 0}%
                </span>
              </div>
              
              <div className="text-center mb-3">
                <div style={{ height: '150px', position: 'relative' }}>
                  <PolarArea
                    data={{
                      labels: ['Completos', 'Pendentes', 'Frustrados', 'Cancelados'],
                      datasets: [
                        {
                          data: [
                            dashboardData?.logisticsEfficiency?.completed || 0,
                            dashboardData?.logisticsEfficiency?.pending || 0,
                            dashboardData?.logisticsEfficiency?.frustrated || 0,
                            dashboardData?.logisticsEfficiency?.cancelled || 0
                          ],
                          backgroundColor: [
                            'rgba(0, 123, 255, 0.7)',   // Azul
                            'rgba(255, 193, 7, 0.7)',   // Amarelo  
                            'rgba(220, 53, 69, 0.7)',   // Vermelho
                            'rgba(108, 117, 125, 0.7)'  // Cinza
                          ],
                          borderColor: ['#007bff', '#ffc107', '#dc3545', '#6c757d'],
                          borderWidth: 2,
                        }
                      ]
                    }}
                    options={{
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          titleColor: 'white',
                          bodyColor: 'white',
                          cornerRadius: 6,
                          callbacks: {
                            label: function(context) {
                              return `${context.label}: ${context.parsed}%`;
                            }
                          }
                        }
                      },
                      scales: {
                        r: {
                          grid: {
                            color: currentTheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                          },
                          angleLines: {
                            color: currentTheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.1)'
                          },
                          ticks: {
                            color: currentTheme === 'dark' ? '#b3b3b3' : '#6b7280',
                            backdropColor: 'transparent'
                          }
                        }
                      }
                    }}
                  />
                  {/* Valor central */}
                  <div 
                    className="position-absolute d-flex flex-column align-items-center justify-content-center"
                    style={{ 
                      top: '50%', 
                      left: '50%', 
                      transform: 'translate(-50%, -50%)',
                      pointerEvents: 'none'
                    }}
                  >
                    <strong style={{ fontSize: '14px', lineHeight: '1', }}>
                      {dashboardData?.logisticsEfficiency?.completed || 0}%
                    </strong>
                  </div>
                </div>
              </div>
              
              <div style={{ fontSize: '11px' }}>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center">
                    <div className="rounded me-2" style={{ width: '8px', height: '8px', backgroundColor: '#28a745' }}></div>
                    <span>Completos</span>
                  </div>
                  <span className="fw-medium">{dashboardData?.logisticsEfficiency?.completed || 0}%</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center">
                    <div className="rounded me-2" style={{ width: '8px', height: '8px', backgroundColor: '#ffc107' }}></div>
                    <span>Pendentes</span>
                  </div>
                  <span className="fw-medium">{dashboardData?.logisticsEfficiency?.pending || 0}%</span>
                </div>
                <div className="d-flex justify-content-between align-items-center mb-2">
                  <div className="d-flex align-items-center">
                    <div className="rounded me-2" style={{ width: '8px', height: '8px', backgroundColor: '#dc3545' }}></div>
                    <span>Frustrados</span>
                  </div>
                  <span className="fw-medium">{dashboardData?.logisticsEfficiency?.frustrated || 0}%</span>
                </div>
                <div className="d-flex justify-content-between align-items-center">
                  <div className="d-flex align-items-center">
                    <div className="rounded me-2" style={{ width: '8px', height: '8px', backgroundColor: '#6c757d' }}></div>
                    <span>Cancelados</span>
                  </div>
                  <span className="fw-medium">{dashboardData?.logisticsEfficiency?.cancelled || 0}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Formas de Pagamento */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-header dashboard-card-header bg-white border-bottom-0 pb-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="card-title mb-0 d-flex align-items-center" style={{ fontSize: '14px', fontWeight: '600', display: 'block' }}>
                  <Icon icon="mdi:credit-card" className="me-2 text-primary" style={{ fontSize: '16px' }} />
                  Formas de Pagamento
                </span>
              </div>
            </div>
            <div className="card-body pt-2">
              <div className="text-center">
                <div style={{ height: '280px' }}>
                  {dashboardData?.salesByPaymentMethod && dashboardData.salesByPaymentMethod.length > 0 ? (
                    <Bar
                      data={{
                        labels: dashboardData.salesByPaymentMethod.map(item => {
                          // Mapear nomes mais amigáveis
                          const methodNames = {
                            'credit_card': 'Cartão de Crédito',
                            'boleto': 'Boleto',
                            'pix': 'Pix',
                            'debit_card': 'Cartão de Débito',
                            'bank_transfer': 'Transferência'
                          };
                          return methodNames[item.payment_method] || item.payment_method;
                        }),
                        datasets: [{
                          data: dashboardData.salesByPaymentMethod.map(item => {
                            const total = dashboardData.salesByPaymentMethod.reduce((sum, i) => sum + i.count, 0);
                            return total > 0 ? ((item.count / total) * 100).toFixed(1) : 0;
                          }),
                          backgroundColor: [
                            'rgba(54, 162, 235, 0.8)',   // Azul - Cartão de Crédito
                            'rgba(255, 206, 86, 0.8)',   // Amarelo - Boleto
                            'rgba(75, 192, 192, 0.8)',   // Verde - Pix
                            'rgba(153, 102, 255, 0.8)',  // Roxo - Cartão de Débito
                            'rgba(255, 99, 132, 0.8)'    // Rosa - Transferência
                          ],
                          borderColor: [
                            'rgba(54, 162, 235, 1)',
                            'rgba(255, 206, 86, 1)',
                            'rgba(75, 192, 192, 1)',
                            'rgba(153, 102, 255, 1)',
                            'rgba(255, 99, 132, 1)'
                          ],
                          borderWidth: 1
                        }]
                      }}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: { display: false },
                          tooltip: {
                            backgroundColor: 'rgba(0, 0, 0, 0.8)',
                            titleColor: 'white',
                            bodyColor: 'white',
                            cornerRadius: 6,
                            callbacks: {
                              label: function(context) {
                                return `${context.parsed.y}% das vendas`;
                              }
                            }
                          }
                        },
                        scales: {
                          y: {
                            beginAtZero: true,
                            max: 60,
                            ticks: {
                              callback: function(value) {
                                return value + '%';
                              },
                              font: { size: 11 }
                            },
                            grid: { color: 'rgba(0,0,0,0.1)' }
                          },
                          x: {
                            ticks: { font: { size: 11 } },
                            grid: { display: false }
                          }
                        }
                      }}
                    />
                  ) : (
                    <div className="d-flex justify-content-center align-items-center h-100 text-muted">
                      <span style={{ fontSize: '12px' }}>Nenhum dado de pagamento disponível</span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Relatório de Comissão */}
        <div className="col-lg-4">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-header dashboard-card-header bg-white border-bottom-0 pb-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="card-title mb-0 d-flex align-items-center" style={{ fontSize: '14px', fontWeight: '600', display: 'block' }}>
                  <Icon icon="mdi:currency-usd" className="me-2 text-primary" style={{ fontSize: '16px' }} />
                  Relatório de Comissão
                </span>
              </div>
            </div>
            <div className="card-body pt-2">
              <div className="d-flex align-items-center justify-content-between mb-3">
                <span className="text-muted" style={{ fontSize: '12px', fontWeight: '500' }}>Taxa de Comissão</span>
                <span className="badge bg-success" style={{ fontSize: '12px' }}>
                  {(() => {
                    const totalSales = (dashboardData?.salesByPaymentMethod || []).reduce((sum, item) => sum + item.count, 0);
                    const completedSales = (dashboardData?.salesByPaymentMethod || []).filter(item => 
                      item.payment_method === 'credit_card' || item.payment_method === 'pix'
                    ).reduce((sum, item) => sum + item.count, 0);
                    return totalSales > 0 ? Math.round((completedSales * 100) / totalSales) : 0;
                  })()}%
                </span>
              </div>
              
              <div className="text-center mb-3">
                <div style={{ height: '140px', position: 'relative' }}>
                  <Bar
                    data={{
                      labels: ['Entrega', 'Antecipado', 'Frustrado', 'Chargeback'],
                      datasets: [
                        {
                          data: (() => {
                            const paymentData = dashboardData?.salesByPaymentMethod || [];
                            const methodCounts = {
                              'credit_card': 0,
                              'pix': 0,
                              'boleto': 0,
                              'after_pay': 0
                            };
                            
                            paymentData.forEach(item => {
                              if (methodCounts.hasOwnProperty(item.payment_method)) {
                                methodCounts[item.payment_method] = item.count;
                              }
                            });
                            
                            return [
                              methodCounts.credit_card,
                              methodCounts.pix,
                              methodCounts.boleto,
                              methodCounts.after_pay
                            ];
                          })(),
                          backgroundColor: [
                            'rgba(255, 152, 0, 0.8)',   // Laranja
                            'rgba(63, 81, 181, 0.8)',   // Indigo
                            'rgba(233, 30, 99, 0.8)',   // Rosa
                            'rgba(96, 125, 139, 0.8)'   // Azul acinzentado
                          ],
                          borderColor: ['#ff9800', '#3f51b5', '#e91e63', '#607d8b'],
                          borderWidth: 2,
                          borderRadius: 4,
                        }
                      ]
                    }}
                    options={{
                      indexAxis: 'y',
                      responsive: true,
                      maintainAspectRatio: false,
                      plugins: {
                        legend: { display: false },
                        tooltip: {
                          backgroundColor: 'rgba(0, 0, 0, 0.8)',
                          titleColor: 'white',
                          bodyColor: 'white',
                          cornerRadius: 6,
                          callbacks: {
                            label: function(context) {
                              const value = context.parsed.x || 0;
                              const total = context.dataset.data.reduce((sum, v) => sum + v, 0);
                              const percentage = total > 0 ? Math.round((value * 100) / total) : 0;
                              return `${context.label}: ${value} (${percentage}%)`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              </div>
              
              <div style={{ fontSize: '11px' }}>
                {(() => {
                  const paymentData = dashboardData?.salesByPaymentMethod || [];
                  const total = paymentData.reduce((sum, item) => sum + item.count, 0);
                  
                  const methodMap = {
                    'credit_card': { color: '#28a745', label: 'Pagamento na Entrega', textClass: 'text-success' },
                    'pix': { color: '#007bff', label: 'Pagamento Antecipado', textClass: 'text-primary' },
                    'boleto': { color: '#dc3545', label: 'Pagamento Frustrado', textClass: 'text-danger' },
                    'after_pay': { color: '#343a40', label: 'Chargeback | Reembolso', textClass: 'text-dark' }
                  };
                  
                  return paymentData.map((item, index) => {
                    const method = methodMap[item.payment_method] || { 
                      color: '#6c757d', 
                      label: 'Outros', 
                      textClass: 'text-muted' 
                    };
                    const percentage = total > 0 ? Math.round((item.count * 100) / total) : 0;
                    
                    return (
                      <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                        <div className="d-flex align-items-center">
                          <div className="rounded me-2" style={{ width: '8px', height: '8px', backgroundColor: method.color }}></div>
                          <span>{method.label}</span>
                        </div>
                        <span className={`fw-medium ${method.textClass}`}>{percentage}%</span>
                      </div>
                    );
                  });
                })()}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Terceira Linha - Faturamento & Saque + Vendas Recentes */}
      <div className="row g-3" style={{ marginBottom: '24px' }}>
        {/* Faturamento & Saque */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-header dashboard-card-header bg-white border-bottom-0 pb-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="card-title mb-0 d-flex align-items-center" style={{ fontSize: '14px', fontWeight: '600', display: 'block' }}>
                  <Icon icon="mdi:chart-bar" className="me-2 text-primary" style={{ fontSize: '16px' }} />
                  Faturamento & Saque
                </span>
                <select 
                  className="form-select form-select-sm" 
                  style={{ width: '110px', fontSize: '12px' }}
                >
                  <option value="week">Semana</option>
                  <option value="month">Mês</option>
                </select>
              </div>
            </div>
            <div className="card-body pt-2">
              {/* Métricas de Resumo */}
              <div className="row g-2 mb-3">
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <div className="rounded me-2" style={{ width: '8px', height: '8px', backgroundColor: '#ff6b35' }}></div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600' }}>Faturado</div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#ff6b35' }}>
                        {formatCurrencyLocal(dashboardData?.totalRevenue || 0)}
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-6">
                  <div className="d-flex align-items-center">
                    <div className="rounded me-2" style={{ width: '8px', height: '8px', backgroundColor: '#28a745' }}></div>
                    <div>
                      <div style={{ fontSize: '12px', fontWeight: '600' }}>Sacado</div>
                      <div style={{ fontSize: '14px', fontWeight: '700', color: '#28a745' }}>
                        {formatCurrencyLocal(dashboardData?.totalWithdrawals || 0)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Gráfico de Barras */}
              <div style={{ height: '200px' }}>
                <Bar
                  data={{
                    labels: ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'],
                    datasets: [
                      {
                        label: 'Faturado',
                        data: dashboardData?.weeklyBillingData?.length > 0 
                          ? dashboardData.weeklyBillingData.map(day => day.revenue)
                          : [0, 0, 0, 0, 0, 0, 0],
                        backgroundColor: '#ff6b35',
                        borderColor: '#ff6b35',
                        borderWidth: 1,
                        borderRadius: 4,
                        borderSkipped: false,
                      },
                      {
                        label: 'Sacado',
                        data: dashboardData?.weeklyBillingData?.length > 0 
                          ? dashboardData.weeklyBillingData.map(day => day.withdrawals)
                          : [0, 0, 0, 0, 0, 0, 0],
                        backgroundColor: '#28a745',
                        borderColor: '#28a745',
                        borderWidth: 1,
                        borderRadius: 4,
                        borderSkipped: false,
                      }
                    ]
                  }}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false
                      },
                      tooltip: {
                        backgroundColor: 'rgba(0, 0, 0, 0.8)',
                        titleColor: 'white',
                        bodyColor: 'white',
                        cornerRadius: 6,
                        callbacks: {
                          label: function(context) {
                            return `${context.dataset.label}: ${formatCurrencyLocal(context.parsed.y)}`;
                          }
                        }
                      }
                    },
                    scales: {
                      x: {
                        grid: {
                          display: false
                        },
                        ticks: {
                          font: {
                            size: 10
                          },
                          color: currentTheme === 'dark' ? '#b3b3b3' : '#6b7280'
                        }
                      },
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: currentTheme === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.05)',
                          drawBorder: false
                        },
                        ticks: {
                          font: {
                            size: 10
                          },
                          color: currentTheme === 'dark' ? '#b3b3b3' : '#6b7280',
                          callback: function(value) {
                            return formatCurrencyLocal(value);
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Vendas Recentes */}
        <div className="col-lg-6">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: '12px' }}>
            <div className="card-header dashboard-card-header bg-white border-bottom-0 pb-2">
              <div className="d-flex justify-content-between align-items-center">
                <span className="card-title mb-0 d-flex align-items-center" style={{ fontSize: '14px', fontWeight: '600', display: 'block' }}>
                  <Icon icon="mdi:clock-outline" className="me-2 text-primary" style={{ fontSize: '16px' }} />
                  Vendas Recentes
                </span>
              </div>
            </div>
            <div className="card-body pt-2">
              {dashboardData?.recentSales?.length > 0 ? (
                <div className="table-responsive">
                  <table className="table table-hover mb-0" style={{ fontSize: '12px' }}>
                    <thead>
                      <tr style={{ backgroundColor: '#f8f9fa' }}>
                        <th style={{ fontSize: '11px', fontWeight: '600', padding: '12px 8px', borderRadius: '8px 0 0 0' }}>Cliente</th>
                        <th style={{ fontSize: '11px', fontWeight: '600', padding: '12px 8px' }}>Produto</th>
                        <th style={{ fontSize: '11px', fontWeight: '600', padding: '12px 8px' }}>Valor</th>
                        <th style={{ fontSize: '11px', fontWeight: '600', padding: '12px 8px' }}>Comissão</th>
                        <th style={{ fontSize: '11px', fontWeight: '600', padding: '12px 8px' }}>Status</th>
                        <th style={{ fontSize: '11px', fontWeight: '600', padding: '12px 8px', borderRadius: '0 8px 0 0' }}>Data</th>
                      </tr>
                    </thead>
                    <tbody>
                      {dashboardData.recentSales.map((sale) => (
                        <tr key={sale.id}>
                          <td style={{ padding: '12px 8px', fontSize: '12px' }}>
                            <div className="fw-medium">{sale.customer_name || 'Cliente não identificado'}</div>
                            <small className="text-muted" style={{ fontSize: '10px' }}>
                              {sale.customer_email || 'cliente@email.com'}
                            </small>
                          </td>
                          <td style={{ padding: '12px 8px', fontSize: '12px' }}>{sale.product_name || 'Produto removido'}</td>
                          <td style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600' }}>{formatCurrencyLocal(sale.total_amount || 0)}</td>
                          <td
                            style={{ padding: '12px 8px', fontSize: '12px', fontWeight: '600', color: '#6c757d' }}
                            title="Comissão por venda ainda não exposta pelo backend V2 nesta listagem."
                          >
                            —
                          </td>
                          <td style={{ padding: '12px 8px' }}>
                            <span className={`badge ${
                              sale.status === 'completed' ? 'bg-success' :
                              sale.status === 'pending' ? 'bg-warning' :
                              sale.status === 'processing' ? 'bg-info' : 'bg-danger'
                            }`} style={{ fontSize: '10px', padding: '4px 8px' }}>
                              {sale.status === 'completed' ? 'Concluída' :
                               sale.status === 'pending' ? 'Pendente' :
                               sale.status === 'processing' ? 'Processando' : 'Cancelada'}
                            </span>
                          </td>
                          <td style={{ padding: '12px 8px', fontSize: '12px', color: '#6c757d' }}>
                            {new Date(sale.created_at).toLocaleDateString('pt-BR')}
                            <br />
                            <small style={{ fontSize: '10px' }}>
                              {new Date(sale.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                            </small>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-4">
                  <Icon icon="mdi:sale-outline" className="text-muted" style={{ fontSize: '32px' }} />
                  <p className="text-muted mt-2" style={{ fontSize: '12px' }}>Nenhuma venda encontrada</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashBoardLayerTen;
